import * as React from 'react'
import { AlertTriangle, CheckCircle2, HelpCircle, Info, Trash2 } from 'lucide-react'
import { cn } from '@shared/utils'
import { Button } from './button'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './dialog'

export type ConfirmVariant = 'default' | 'danger' | 'warning' | 'info' | 'success'

export interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title?: string
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  variant?: ConfirmVariant
  icon?: React.ReactNode
  loading?: boolean
}

const variantMap: Record<
  ConfirmVariant,
  { Icon: React.ElementType; iconCls: string; ringCls: string; btnCls: string }
> = {
  default: {
    Icon: HelpCircle,
    iconCls: 'text-muted-foreground',
    ringCls: 'bg-secondary',
    btnCls: '',
  },
  danger: {
    Icon: Trash2,
    iconCls: 'text-state-critical',
    ringCls: 'bg-state-critical-bg',
    btnCls: 'bg-destructive hover:bg-destructive/90 focus-visible:ring-destructive text-destructive-foreground',
  },
  warning: {
    Icon: AlertTriangle,
    iconCls: 'text-state-warn',
    ringCls: 'bg-state-warn-bg',
    btnCls: 'bg-state-warn hover:bg-state-warn/90 focus-visible:ring-state-warn text-white',
  },
  info: {
    Icon: Info,
    iconCls: 'text-state-info',
    ringCls: 'bg-state-info-bg',
    btnCls: 'bg-state-info hover:bg-state-info/90 focus-visible:ring-state-info text-white',
  },
  success: {
    Icon: CheckCircle2,
    iconCls: 'text-state-ok',
    ringCls: 'bg-state-ok-bg',
    btnCls: 'bg-state-ok hover:bg-state-ok/90 focus-visible:ring-state-ok text-white',
  },
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Xác nhận',
  description,
  confirmLabel = 'Xác nhận',
  cancelLabel = 'Hủy',
  variant = 'default',
  icon,
  loading = false,
}: ConfirmDialogProps) {
  const { Icon, iconCls, ringCls, btnCls } = variantMap[variant]

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-sm gap-0 p-0 overflow-hidden rounded-lg">
        <div className="flex flex-col items-center px-8 pt-8 pb-6 text-center gap-4">
          <div className={cn('flex items-center justify-center rounded-full size-14 shrink-0', ringCls)}>
            {icon ?? <Icon className={cn('size-7', iconCls)} />}
          </div>

          <div className="space-y-2">
            <DialogTitle className="text-base font-semibold text-foreground leading-snug">
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
                {description}
              </DialogDescription>
            )}
          </div>
        </div>

        <div className="flex gap-3 border-t border-border px-6 py-4">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            className={cn('flex-1', btnCls)}
            onClick={onConfirm}
            isLoading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
